import { AlertCircle, Calendar, Link } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useCalendlyConfig } from '../../../hooks/useCalendlyConfig';

export function CalendlySettings() {
  const { chatbotPublicId } = useChatbot();
  const { config, setConfig, loading, initialized, initializeConfig } = useCalendlyConfig();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize the configuration when the component mounts
  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initializeConfig(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initializeConfig]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!chatbotPublicId) return;

    try {
      setError(null);
      setSuccess(false);
      
      const formData = new FormData(e.currentTarget);
      const url = (formData.get('calendlyUrl') as string).trim();

      // Validation basique de l'URL Calendly
      if (url && !url.includes('calendly.com')) {
        setError('URL must be a valid Calendly URL');
        return;
      }

      const success = await setConfig({ url }, chatbotPublicId);
      if (success) {
        setSuccess(true);
      } else {
        setError('Error saving configuration');
      }
    } catch (err) {
      console.error('Error saving Calendly settings:', err);
      setError(err instanceof Error ? err.message : 'An error occurred while saving');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">Calendly configurations</h2>
        <p className="text-gray-400">Connect your Calendly calendar to allow users to schedule appointments directly from the chatbot.</p>
      </div>

      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <div className="flex items-center gap-3 mb-6">
          <Calendar className="w-6 h-6 text-indigo-400" />
          <h3 className="text-lg font-semibold text-white">Calendly settings</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p>{error}</p>
            </div>
          )}

          {success && (
            <div className="bg-green-500/10 text-green-400 p-4 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p>Configuration successfully saved</p>
            </div>
          )}

          <div>
            <label htmlFor="calendlyUrl" className="block text-sm font-medium text-gray-300 mb-2">
              Calendly link
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Link className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="url"
                id="calendlyUrl"
                name="calendlyUrl"
                defaultValue={config.url}
                placeholder="https://calendly.com/votre-nom"
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <p className="mt-2 text-sm text-gray-400">
              Enter the URL of your Calendly page. Users will be able to schedule appointments directly from the chatbot.
            </p>
          </div>

          <div className="bg-indigo-500/10 rounded-lg p-4">
            <h4 className="text-indigo-400 font-medium mb-2">How to set up?</h4>
            <ol className="list-decimal list-inside text-sm text-gray-400 space-y-2">
              <li>Log in to your Calendly account</li>
              <li>Go to your profile page</li>
              <li>Copy the URL of your booking page</li>
              <li>Paste the URL in the field above</li>
            </ol>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-indigo-500/20 text-indigo-400 px-4 py-2 rounded-lg hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}